// Shared declarative rule evaluator — mirrors backend onboarding/templates.py
export function lookup(ref, ctx, local) {
  if (ref.startsWith('@')) {
    const [sec, key] = ref.slice(1).split('.');
    return (ctx[sec] || {})[key];
  }
  return local[ref];
}

export function conditionPasses(c, ctx, local) {
  const v = lookup(c.field, ctx, local);
  switch (c.op) {
    case 'eq': return v === c.value;
    case 'neq': return v !== undefined && v !== null && v !== c.value;
    case 'in': return (c.value || []).includes(v);
    case 'truthy': return !!v;
    case 'lt': return Number(v) < Number(c.value);
    default: return false;
  }
}

export const fieldVisible = (field, ctx, local) => (field.show_if || []).every((c) => conditionPasses(c, ctx, local));

export const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);

export function missingRequired(section, values, ctx) {
  const missing = [];
  for (const f of section.fields) {
    if (!fieldVisible(f, ctx, values)) continue;
    const v = values[f.key];
    if (f.type === 'repeat') {
      const rows = Array.isArray(v) ? v : [];
      if (f.required && !rows.length) missing.push(f.key);
      rows.forEach((row, i) => f.fields.forEach((sub) => {
        if (fieldVisible(sub, ctx, row) && sub.required && isEmpty(row[sub.key])) missing.push(`${f.key}[${i}].${sub.key}`);
      }));
      continue;
    }
    if (f.required && isEmpty(v)) missing.push(f.key);
  }
  return missing;
}

export const valuesFromAnswers = (answers) => Object.fromEntries(Object.entries(answers || {}).map(([k, m]) => [k, m?.value]));

// ── Applicant journey (mirrors backend _journey / section_applies / fields_for_slot) ──
export const DEFAULT_APPLICANTS = { personal: 'both', address: 'both', employment: 'both', other_income: 'both' };
export const sectionApplicants = (s) => (s.scope === 'shared' ? 'shared' : s.applicants || DEFAULT_APPLICANTS[s.key] || 'primary');
export const sectionApplies = (s, slot) => { const a = sectionApplicants(s); return a === 'shared' || a === 'both' || (a === 'primary' && slot === 1) || (a === 'co' && slot === 2); };
export const fieldsForSlot = (s, slot) => (s.fields || []).filter((f) => ['both', slot === 1 ? 'primary' : 'co'].includes(f.for_applicant || 'both'));
export const stepKey = (key, slot) => (slot === 2 ? `${key}@2` : key);
export const APPLICANT_LABEL = { 1: 'Applicant 1 · You', 2: 'Applicant 2 · Second applicant' };

// Expand template sections into the ordered steps a customer walks through.
// Joint → Applicant 1 finishes every per-applicant section, then Applicant 2's block, then the remaining sections.
export function buildJourney(sections, joint) {
  const steps = []; let pending = [];
  for (const s of sections) {
    const a2 = joint && s.scope !== 'shared' && sectionApplies(s, 2);
    if (pending.length && !a2) { steps.push(...pending); pending = []; }
    if (s.scope === 'shared') steps.push({ ...s, step_key: s.key, applicant_slot: 1, applicant_label: null });
    else if (sectionApplies(s, 1)) steps.push({ ...s, fields: fieldsForSlot(s, 1), step_key: s.key, applicant_slot: 1, applicant_label: joint ? APPLICANT_LABEL[1] : null });
    if (a2) pending.push({ ...s, fields: fieldsForSlot(s, 2), step_key: stepKey(s.key, 2), applicant_slot: 2, applicant_label: APPLICANT_LABEL[2] });
  }
  return [...steps, ...pending];
}

// Rule context for one applicant slot: that applicant's sections + shared sections, keyed by section key.
export function ctxForSlot(steps, slot) {
  const ctx = {};
  for (const s of steps) if (s.scope === 'shared' || (s.applicant_slot || 1) === slot) ctx[s.key] = valuesFromAnswers(s.answers);
  return ctx;
}

export const fmtMoney = (n) => (n === undefined || n === null || n === '' ? '—' : new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(Number(n)));
export const fmtDate = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return iso; } };
export const fmtDateTime = (iso) => { if (!iso) return '—'; try { return new Date(iso).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch { return iso; } };

export const STATE_LABEL = {
  todo: 'To do', in_progress: 'In progress', needs_confirmation: 'Please confirm', complete: 'Done', needs_correction: 'Flagged',
};
export const DOC_STATE_LABEL = {
  requested: 'Requested', uploaded: 'Uploaded — awaiting review', processing: 'With your broker', needs_replacement: 'Needs replacement', accepted: 'Accepted', waived: 'Not required',
};

// Genuine completion: answered required visible questions / all applicable required questions (+ documents separately).
export function computeProgress(payload) {
  const steps = payload.sections;
  const ctxBy = { 1: ctxForSlot(steps, 1), 2: ctxForSlot(steps, 2) };
  let total = 0; let answered = 0;
  const perSection = {};
  for (const s of steps) {
    const ctx = ctxBy[s.applicant_slot || 1];
    const vals = valuesFromAnswers(s.answers);
    let t = 0; let a = 0;
    if (s.shared_readonly) { t = 1; a = s.state === 'complete' ? 1 : 0; }
    else {
      for (const f of s.fields || []) {
        if (!f.required || !fieldVisible(f, ctx, vals)) continue;
        t += 1;
        const v = vals[f.key];
        if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) a += 1;
      }
    }
    perSection[s.step_key || s.key] = { total: t, answered: a, pct: t ? Math.round((100 * a) / t) : (s.state === 'complete' ? 100 : 0) };
    total += t; answered += a;
  }
  const docs = (payload.documents || []).filter((d) => d.required);
  const docsDone = docs.filter((d) => ['uploaded', 'under_review', 'processing', 'accepted', 'waived'].includes(d.status) || (d.files || []).some((f) => !f.superseded)).length;
  return {
    factfind: total ? Math.round((100 * answered) / total) : 0, answered, total, perSection,
    docs: docs.length ? Math.round((100 * docsDone) / docs.length) : 100, docsDone, docsTotal: docs.length,
    consent: !!payload.consent_done,
  };
}
